import { Box, IconButton, alpha, styled, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Icon, Tooltip } from '@verantos-dev/shared-components';

import {
  CohortBuilderViewField,
  CohortBuilderViewFieldProps,
} from '../CohortBuilderViewField/CohortBuilderViewField';

export type CohortBuilderItemViewCardProps = {
  /**
   * The id of the cohort builder item.
   */
  id: string;

  /**
   * Whether or not the cohort builder item is in use. This will render a check icon.
   */
  isInUse: boolean;

  /**
   * @prop {number[]} this widthOfFixPart of component of the fix parts of a entry
   * @default [300]
   */
  width?: number[];

  /**
   * The names of items using this cohort builder item.
   */
  inUseCohortBuilderItems?: string[];

  /**
   * The fields to display in the view card.
   */
  fields: CohortBuilderViewFieldProps[];

  /**
   * To display the card in the view mode.
   * @default false
   */
  viewMode: boolean;

  /**
   * The function to call when the delete icon is clicked.
   */
  onDeleteClick: (id: string) => void;

  /**
   * The function to call when the edit icon is clicked.
   */
  onEditClick: (id: string) => void;
};

const CustomIconButton = styled(IconButton)(() => ({
  '&.MuiButtonBase-root.MuiIconButton-root': {
    pointerEvents: 'auto',
    '&:hover ': {
      width: '40px',
      height: '40px',
      backgroundColor: alpha('#d3d4d7', 0.6),
      borderRadius: '4px',
    },
  },
}));

export const MULTIPLE_ENTRIES_WIDTH = ['auto', 318];

export const CohortBuilderItemViewCard = ({
  id,
  isInUse,
  inUseCohortBuilderItems,
  fields,
  onEditClick,
  onDeleteClick,
  viewMode,
}: CohortBuilderItemViewCardProps) => {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <Box
      key={`cohortBuilderItemViewCard-${id}`}
      data-testid={`cohortBuilderItemViewCard-${id}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        height: theme.spacing(20),
        width: '100%',
        backgroundColor: theme.palette.cardBackground?.main,
        px: theme.spacing(6),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(2),
        boxShadow: 'none',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
        <Box sx={{ mr: theme.spacing(6), width: theme.spacing(6) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '24px' }}>
            {isInUse && (
              <Tooltip
                id={`cohortBuilderItemViewCard-inUse-toolip${id}`}
                arrow
                placement="bottom"
                width="auto"
                title={
                  <>
                    <Box>
                      {intl.formatMessage({
                        id: 'projects.cohortBuilder.cohortBuilderItemViewCard.inUse',
                      })}
                    </Box>
                    {inUseCohortBuilderItems?.map((item, index) => (
                      <Box key={item} id={`inUseItem-${index}`}>
                        {` - ${item}`}
                      </Box>
                    ))}
                  </>
                }
              >
                <Icon
                  id={`cohortBuilderItemViewCard-inUse-${id}`}
                  icon="inUse"
                  width={24}
                  height={24}
                />
              </Tooltip>
            )}
            {!isInUse && (
              <Tooltip
                id={`cohortBuilderItemViewCard-notInUse-tooltip-${id}`}
                arrow
                placement="bottom"
                width="auto"
                title={intl.formatMessage({
                  id: 'projects.cohortBuilder.cohortBuilderItemViewCard.notInUse',
                })}
              >
                <Icon
                  id={`cohortBuilderItemViewCard-inUse-${id}`}
                  icon="notInUse"
                  width={24}
                  height={24}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flex: 1 }}>
          {fields.map(({ id, label, value }, index) => {
            const width = fields.length === 1 ? 'aut0' : MULTIPLE_ENTRIES_WIDTH[index];
            if (index === 0) {
              return (
                <Box
                  key={`viewField-${id}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flex: 1,
                    gap: theme.spacing(6),
                  }}
                >
                  <CohortBuilderViewField key={id} id={id} label={label} value={value} />
                </Box>
              );
            }
            return (
              <div key={`viewField-${id}`} style={{ width }}>
                <CohortBuilderViewField
                  key={id}
                  id={id}
                  label={label}
                  value={value}
                  width={width}
                />
              </div>
            );
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(6),
          }}
        >
          {viewMode && (
            <CustomIconButton
              aria-label="open"
              name={'open'}
              size="medium"
              onClick={() => onEditClick(id)}
            >
              <Icon
                id={`cohortBuilderItemViewCard-view-${id}`}
                icon={'open'}
                width={24}
                height={24}
              />
            </CustomIconButton>
          )}
          {!viewMode && (
            <CustomIconButton
              aria-label="edit"
              name={'Edit'}
              size="medium"
              onClick={() => onEditClick(id)}
            >
              <Icon
                id={`cohortBuilderItemViewCard-edit-${id}`}
                icon={`edit`}
                width={24}
                height={24}
              />
            </CustomIconButton>
          )}

          {!viewMode && (
            <Tooltip
              id={`cohortBuilderItemViewCard-delete-tooltip-${id}`}
              width={'auto'}
              arrow
              title={
                isInUse
                  ? intl.formatMessage({
                      id: 'projects.cohortBuilder.cohortBuilderItemViewCard.deleteInUse',
                    })
                  : intl.formatMessage({
                      id: 'projects.cohortBuilder.cohortBuilderItemViewCard.deleteNotInUse',
                    })
              }
            >
              <CustomIconButton
                aria-label="delete"
                size="medium"
                name="Delete"
                disabled={isInUse}
                onClick={() => !isInUse && onDeleteClick(id)}
              >
                <Icon
                  id={`cohortBuilderItemViewCard-delete-${id}`}
                  icon="delete"
                  width={24}
                  height={24}
                />
              </CustomIconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};
